import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import blogService from '../services/blogService';
import { getImageUrl } from '../utils/imageUrl';
import {
    FiLoader,
    FiAlertCircle,
    FiArrowLeft,
    FiCheckCircle,
    FiClock,
    FiTag,
    FiShield,
    FiMaximize2
} from 'react-icons/fi';
import { useLightbox } from '../context/LightboxContext';
import './BlogDetailPage.css';

const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

/**
 * Parses markdown-like blog body into clean, rich editorial components
 */
const renderFormattedBlogContent = (content) => {
    if (!content) return null;

    const lines = content.split('\n');
    const elements = [];
    let currentList = [];
    let keyIdx = 0;

    const flushList = () => {
        if (currentList.length > 0) {
            elements.push(
                <div key={`list-${keyIdx++}`} className="blog-highlights-box">
                    <ul className="blog-highlights-list">
                        {currentList.map((item, i) => (
                            <li key={i} className="blog-highlight-item">
                                <div className="highlight-icon-wrap">
                                    <FiCheckCircle className="highlight-icon" />
                                </div>
                                <div className="highlight-text">
                                    {item.label && <strong className="highlight-label">{item.label}: </strong>}
                                    <span>{item.desc}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            );
            currentList = [];
        }
    };

    for (let i = 0; i < lines.length; i++) {
        const rawLine = lines[i].trim();

        if (!rawLine) {
            flushList();
            continue;
        }

        // H3 Heading (### Title)
        if (rawLine.startsWith('### ')) {
            flushList();
            const headingText = rawLine.replace(/^###\s+/, '');
            elements.push(
                <h3 key={`h3-${keyIdx++}`} className="blog-section-h3">
                    <span className="h3-accent-bar" />
                    {headingText}
                </h3>
            );
            continue;
        }

        // H2 Heading (## Title)
        if (rawLine.startsWith('## ')) {
            flushList();
            const headingText = rawLine.replace(/^##\s+/, '');
            elements.push(
                <h2 key={`h2-${keyIdx++}`} className="blog-section-h2">
                    {headingText}
                </h2>
            );
            continue;
        }

        // Bullet item (- **Title**: Description or - Text)
        if (rawLine.startsWith('- ') || rawLine.startsWith('* ')) {
            const listText = rawLine.replace(/^[-*]\s+/, '');
            const boldMatch = listText.match(/^\*\*(.*?)\*\*:\s*(.*)$/);

            if (boldMatch) {
                currentList.push({
                    label: boldMatch[1],
                    desc: boldMatch[2],
                });
            } else {
                currentList.push({
                    label: '',
                    desc: listText.replace(/\*\*(.*?)\*\*/g, '$1'),
                });
            }
            continue;
        }

        flushList();

        // Check if paragraph is a callout box (e.g. installment / discount notice)
        const isOfferCallout =
            rawLine.toLowerCase().includes('installment plan') ||
            rawLine.toLowerCase().includes('discount') ||
            rawLine.toLowerCase().includes('rahber corporation');

        if (isOfferCallout && i === lines.length - 1) {
            elements.push(
                <div key={`callout-${keyIdx++}`} className="blog-offer-callout">
                    <div className="callout-icon-box">
                        <FiShield />
                    </div>
                    <div className="callout-text-content">
                        <h4>Authentic Showroom Guarantee</h4>
                        <p>{rawLine}</p>
                    </div>
                </div>
            );
        } else {
            elements.push(
                <p key={`p-${keyIdx++}`} className="blog-paragraph">
                    {rawLine}
                </p>
            );
        }
    }

    flushList();
    return elements;
};

const BlogDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { openLightbox } = useLightbox();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!slug) {
                setError('Invalid link.');
                setLoading(false);
                return;
            }
            setLoading(true);
            setError('');
            try {
                const data = await blogService.getBySlug(slug);
                if (!cancelled) {
                    if (data) setBlog(data);
                    else setError('Post not found.');
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || 'Could not load this post.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    if (loading) {
        return (
            <div className="blog-detail-page blog-detail-loading">
                <div className="container">
                    <FiLoader className="spin-icon" />
                    <p>Loading buying guide…</p>
                </div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="blog-detail-page">
                <div className="container blog-detail-error">
                    <FiAlertCircle />
                    <p>{error || 'Post not found.'}</p>
                    <Link to="/blogs" className="btn btn-primary">
                        Back to blogs
                    </Link>
                </div>
            </div>
        );
    }

    const heroImage = getImageUrl(blog.featured_image || blog.image);

    return (
        <article className="blog-detail-page">
            <div className="container blog-detail-container">
                {/* Back Link */}
                <div className="blog-detail-top-nav">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="blog-back-btn"
                        aria-label="Go back to previous page"
                    >
                        <FiArrowLeft /> Back to Buying Guides
                    </button>
                </div>

                {/* Article Header */}
                <header className="blog-detail-header">
                    <div className="blog-detail-meta-pill-row">
                        <span className="blog-detail-category-badge">
                            <FiTag className="meta-icon" /> {blog.category || 'Buying Guide'}
                        </span>
                        <span className="blog-detail-date">
                            <FiClock className="meta-icon" /> {formatDate(blog.published_at)}
                        </span>
                        <span className="blog-detail-reading-time">
                            5 min read • Verified Advice
                        </span>
                    </div>

                    <h1 className="blog-detail-title">{blog.title}</h1>

                    {blog.excerpt && (
                        <p className="blog-detail-lead">{blog.excerpt}</p>
                    )}
                </header>

                {/* Balanced Editorial Hero Image (Never Over-Zoomed) */}
                {heroImage && (
                    <div className="blog-detail-hero-frame">
                        <div
                            className="blog-detail-hero-inner zoomable-hero"
                            onClick={() => openLightbox(heroImage, blog.title, blog.title)}
                            title="Click to view full photo"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    openLightbox(heroImage, blog.title, blog.title);
                                }
                            }}
                        >
                            <img
                                src={heroImage}
                                alt={blog.title}
                                className="blog-detail-hero-img"
                                onError={(e) => {
                                    e.currentTarget.src = 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=1200&auto=format&fit=crop';
                                }}
                            />
                            <div className="hero-zoom-pill">
                                <FiMaximize2 /> Tap to enlarge
                            </div>
                        </div>
                    </div>
                )}

                {/* Richly Formatted Content Body */}
                <div className="blog-detail-article-body">
                    {renderFormattedBlogContent(blog.content)}
                </div>

                {/* Bottom Showroom Action Footer */}
                <footer className="blog-detail-footer">
                    <div className="blog-footer-card">
                        <div className="blog-footer-card-content">
                            <h3>Have Questions About This Appliance?</h3>
                            <p>
                                Visit Rahber Corporation's Lahore flagship showroom to inspect sealed units in person or contact our appliance advisors for installment plans.
                            </p>
                        </div>
                        <div className="blog-footer-card-actions">
                            <Link to="/products" className="btn btn-primary blog-footer-btn">
                                Browse Recommended Models
                            </Link>
                            <Link to="/blogs" className="btn btn-outline blog-footer-btn">
                                Explore More Guides
                            </Link>
                        </div>
                    </div>
                </footer>
            </div>
        </article>
    );
};

export default BlogDetailPage;
